/**
 * Reference Letter API Service
 *
 * The Reference Letter routes of the people-space proxy: the supervisor requests, and the letter
 * Boris drafts from one. The Airtable key lives server-side.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { DoneMark, DraftRequestBody, ReferenceLetterDraft, ReferenceLetterLeavers, ReferenceLetterRequest, SignatureView } from '../types/referenceLetterDraft';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** The proxy's own message when it sent one (a 4xx says what to fix), else the status. */
async function failure(res: Response, what: string): Promise<Error> {
  const body = await res.json().catch(() => null);
  return new Error(body?.error || `${what} (${res.status})`);
}

/** FINN leavers of the last three months (`future`: and every later one), each with their request's status. */
export async function fetchReferenceLetterLeavers(future: boolean): Promise<ReferenceLetterLeavers> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/leavers${future ? '?future=true' : ''}`);
  if (!res.ok) throw await failure(res, 'Failed to load leavers');
  return res.json();
}

/**
 * Boris reminds the leaver's supervisor on Slack. At most once a day per leaver: a 429 carries
 * when the day's reminder went out — reported as `already`, so the page says so instead of
 * claiming a send it did not cause.
 */
export async function pingSupervisor(employeeId: string): Promise<{ pingedAt: string; already: boolean }> {
  // The answers the row explains in words, read here rather than thrown by authenticatedFetch (which
  // would also report them globally): already reminded (429), nothing missing (409), not a leaver
  // (404), no supervisor email (422).
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/leavers/${encodeURIComponent(employeeId)}/ping`, {
    method: 'POST',
    suppressErrorStatuses: [404, 409, 422, 429],
  });
  const body = await res.json().catch(() => null);
  if (res.status === 429 && body?.pingedAt) return { pingedAt: body.pingedAt, already: true };
  if (!res.ok) throw new Error(body?.error || `The reminder could not be sent (${res.status})`);
  if (!body?.pingedAt) throw new Error('The reminder could not be confirmed');
  return { pingedAt: body.pingedAt, already: false };
}

/**
 * The last four months of requests; with `email`, every request for that employee; with
 * `type: 'interim'`, the recent interim ones.
 */
export async function fetchReferenceLetterRequests({ email, type }: { email?: string; type?: 'interim' } = {}): Promise<ReferenceLetterRequest[]> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (type) params.set('type', type);
  const query = params.toString() ? `?${params}` : '';
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/requests${query}`);
  if (!res.ok) throw await failure(res, 'Failed to load requests');
  return (await res.json()).requests;
}

/** Boris's draft for one request plus the reviewer's overrides; nothing is stored server-side. */
export async function fetchReferenceLetterDraft(body: DraftRequestBody): Promise<ReferenceLetterDraft> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/drafts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await failure(res, 'Boris could not draft this letter');
  return res.json();
}

/** The finished .docx. The proxy re-checks the draft and answers 409 if anything is still open. */
export async function downloadReferenceLetterDocx(body: DraftRequestBody): Promise<Blob> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/drafts/docx`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await failure(res, 'Download failed');
  return res.blob();
}

/** A refusal of the signature routes: the proxy's own message, its code, and the letter's state when it said. */
export class SignatureError extends Error {
  readonly code: string | null;
  readonly signature: SignatureView | null;

  constructor(message: string, code: string | null, signature: SignatureView | null) {
    super(message);
    this.name = 'SignatureError';
    this.code = code;
    this.signature = signature;
  }
}

/**
 * One signature call. Its refusals are answers the page explains in words, read here rather than
 * thrown by authenticatedFetch (which would also report them globally): an unknown request (400),
 * open points or already out (409), a letter that could not be prepared (500), YouSign refused or
 * may have sent (502), not set up (503). A 403 is left to the app: it means no access at all.
 */
async function signatureCall(path: string, init: RequestInit = {}): Promise<SignatureView> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/${path}`, { ...init, suppressErrorStatuses: [400, 409, 500, 502, 503] });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new SignatureError(body?.error || `The signature could not be handled (${res.status})`, body?.code ?? null, body?.signature ?? null);
  if (!body?.signature?.state) throw new SignatureError('The proxy did not say where the letter stands', null, null);
  return body.signature;
}

/** Sends the letter on screen to the supervisor through YouSign — the same request the preview came from. */
export function sendForSignature(body: DraftRequestBody): Promise<SignatureView> {
  return signatureCall('drafts/sign', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

/** Where the letter stands, read from YouSign while it is out. */
export function fetchSignature(recordId: string): Promise<SignatureView> {
  return signatureCall(`signatures/${encodeURIComponent(recordId)}`);
}

/** Withdraws a letter that is out for signature; YouSign tells the supervisor. */
export function cancelSignature(recordId: string): Promise<SignatureView> {
  return signatureCall(`signatures/${encodeURIComponent(recordId)}/cancel`, { method: 'POST' });
}

/**
 * Marks a letter Done by hand, or takes the mark back: by its request, or a leaver with none by
 * their Personio record. Answers the mark now (null once taken back).
 */
export async function markLetterDone(letter: { recordId: string } | { personioId: string }, done: boolean): Promise<DoneMark | null> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/done`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...letter, done }),
    // Shown in the menu itself, in the proxy's words; a global toast would say it twice.
    suppressErrorStatuses: [400, 500, 502, 503],
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `The letter could not be marked (${res.status})`);
  return body?.done ?? null;
}

/** Files a signed letter in Personio again, after a filing that certainly failed. */
export function refileInPersonio(recordId: string): Promise<SignatureView> {
  return signatureCall(`signatures/${encodeURIComponent(recordId)}/personio`, { method: 'POST' });
}

/** The signed PDF, once the supervisor has signed. */
export async function downloadSignedPdf(recordId: string): Promise<Blob> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letters/signatures/${encodeURIComponent(recordId)}/pdf`, { suppressErrorStatuses: [409, 502] });
  if (!res.ok) throw await failure(res, 'The signed letter could not be downloaded');
  return res.blob();
}
